import React from 'react';
import { timeAxis } from './timeAxis.js';
import { useChartTooltip } from './useChartTooltip.jsx';

// How sure you were against how often you were right, per set, on the same time axis as
// BrierOverTimeChart.
//
// This is the half of calibration that a single Brier number hides. Brier moves when either
// confidence or accuracy moves, so a set can improve for the wrong reason — hedging everything to
// 60% is a real way to lower it. Here the two lines are separate: confidence above accuracy is
// overconfidence, and the gap between them is the size of it.
//
// Both series are read off folded forecasts, so confidence is "how sure of the side you picked"
// and accuracy is "how often that side won". That's why the axis stops at 50%: a folded forecast
// cannot be less confident than a coin flip. Accuracy can fall below it, and the axis extends if
// it ever does.

// Matches CalibrationChart's box exactly — see the note in BrierOverTimeChart.
const WIDTH = 420;
const HEIGHT = 320;
const PAD = { top: 16, right: 18, bottom: 42, left: 46 };
const PLOT_W = WIDTH - PAD.left - PAD.right;
const PLOT_H = HEIGHT - PAD.top - PAD.bottom;

export default function ConfidenceChart({ points }) {
  const tip = useChartTooltip();
  if (!points || points.length < 2) return null;

  const values = points.flatMap(p => [p.confidence, p.accuracy]).filter(v => v !== null && v !== undefined);
  if (!values.length) return null;
  const yMin = Math.min(0.5, Math.floor(Math.min(...values) * 10) / 10);

  const axis = timeAxis(points, PAD.left, PLOT_W);
  const yScale = v => PAD.top + (1 - (v - yMin) / (1 - yMin)) * PLOT_H;

  const ticks = [];
  for (let t = yMin; t <= 1 + 1e-9; t += 0.1) ticks.push(Math.round(t * 100) / 100);

  const line = key =>
    points
      .map((p, i) => ({ v: p[key], x: axis.x[i] }))
      .filter(p => p.v !== null && p.v !== undefined)
      .map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${yScale(p.v).toFixed(1)}`)
      .join(' ');

  const last = points[points.length - 1];
  const lastX = axis.x[points.length - 1];

  return (
    <figure className="pred-chart" ref={tip.ref}>
      <figcaption className="pred-chart-title">Confidence vs. accuracy</figcaption>
      <p className="pred-legend">
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-primary" aria-hidden="true" />
          Average confidence
        </span>
        <span className="pred-legend-item">
          <span className="pred-legend-swatch is-secondary" aria-hidden="true" />
          Actually right
        </span>
        <span className="pred-legend-note">closer together is better</span>
      </p>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="pred-chart-svg"
        role="img"
        aria-label="Average stated confidence against how often the prediction was right, per set"
      >
        {ticks.map(t => (
          <g key={t}>
            <line
              className="pred-chart-grid"
              x1={PAD.left}
              x2={WIDTH - PAD.right}
              y1={yScale(t)}
              y2={yScale(t)}
            />
            <text className="pred-chart-tick" x={PAD.left - 8} y={yScale(t) + 4} textAnchor="end">
              {Math.round(t * 100)}%
            </text>
          </g>
        ))}

        {/* The gap itself, shaded — it is the quantity the chart is about. */}
        {points.map((p, i) => {
          if (p.confidence === null || p.accuracy === null) return null;
          const next = points[i + 1];
          if (!next || next.confidence === null || next.accuracy === null) return null;
          const x1 = axis.x[i];
          const x2 = axis.x[i + 1];
          return (
            <path
              key={`gap-${p.key}`}
              className="pred-chart-gap"
              d={
                `M${x1},${yScale(p.confidence)} L${x2},${yScale(next.confidence)} ` +
                `L${x2},${yScale(next.accuracy)} L${x1},${yScale(p.accuracy)} Z`
              }
            />
          );
        })}

        <path className="pred-chart-line" d={line('confidence')} />
        <path className="pred-chart-line is-secondary" d={line('accuracy')} />

        {points.map((p, i) => (
          <g key={p.key}>
            {axis.radius > 0 && p.confidence !== null && p.confidence !== undefined && (
              <circle className="pred-chart-point" cx={axis.x[i]} cy={yScale(p.confidence)} r={axis.radius} />
            )}
            {axis.radius > 0 && p.accuracy !== null && p.accuracy !== undefined && (
              <circle
                className="pred-chart-point is-secondary"
                cx={axis.x[i]}
                cy={yScale(p.accuracy)}
                r={axis.radius}
              />
            )}
            {axis.showLabel[i] && (
              <text className="pred-chart-tick" x={axis.x[i]} y={HEIGHT - PAD.bottom + 18} textAnchor="middle">
                {p.label}
              </text>
            )}
            {/* Hover band rather than the marks — see the note in BrierOverTimeChart. */}
            <rect
              x={axis.bands[i].x}
              y={PAD.top}
              width={axis.bands[i].width}
              height={PLOT_H}
              fill="transparent"
              {...tip.bind({
                title: p.fullLabel,
                rows: [
                  ['Confidence', `${Math.round(p.confidence * 100)}%`],
                  ['Actually right', `${Math.round(p.accuracy * 100)}%`],
                  [
                    p.confidence >= p.accuracy ? 'Overconfident by' : 'Underconfident by',
                    `${Math.abs(Math.round((p.confidence - p.accuracy) * 100))} pts`
                  ],
                  ['Graded', p.scored],
                  ['Written', p.writtenOn]
                ]
              })}
            />
          </g>
        ))}

        {last.confidence !== null && last.confidence !== undefined && (
          <text className="pred-chart-value" x={lastX} y={yScale(last.confidence) - 12} textAnchor="end">
            {Math.round(last.confidence * 100)}%
          </text>
        )}
        {last.accuracy !== null && last.accuracy !== undefined && (
          <text
            className="pred-chart-value is-secondary"
            x={lastX}
            y={yScale(last.accuracy) + (last.accuracy > last.confidence ? -12 : 20)}
            textAnchor="end"
          >
            {Math.round(last.accuracy * 100)}%
          </text>
        )}

        <text className="pred-chart-axis" x={PAD.left + PLOT_W / 2} y={HEIGHT - 6} textAnchor="middle">
          {axis.dated ? 'Date written' : 'Set, written oldest first'}
        </text>
        <text
          className="pred-chart-axis"
          transform={`translate(12 ${PAD.top + PLOT_H / 2}) rotate(-90)`}
          textAnchor="middle"
        >
          Probability
        </text>
      </svg>
      {tip.node}
      <p className="pred-chart-note">
        Confidence above accuracy means overconfident. Both are measured on forecasts folded to the
        side you picked, so the axis floor is 50%.
      </p>
    </figure>
  );
}
